import * as Sentry from "@sentry/browser";
import {
  AlertCircle,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { clearErrorMessage, logIn, logOut, saveToken } from "@/actions/auth";
import * as routes from "@/constants/routes";
import { strings } from "@/I18n";
import {
  firebaseLogin,
  setupRecaptcha,
  verifyLoginOTP,
} from "@/utils/firebase";
import { getWhiteLabelLogo } from "@/utils/utils";
import { Alert, AlertDescription } from "@/v2/ui/primitives/alert";
import { Button } from "@/v2/ui/primitives/button";
import { Input } from "@/v2/ui/primitives/input";
import { Label } from "@/v2/ui/primitives/label";

interface FirebaseUser {
  user: {
    email?: string;
    getIdToken: () => Promise<string>;
  };
}

const mapDispatchToProps = {
  logIn,
  saveToken,
  logOut,
  clearErrorMessage,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const LoginScreen: React.FC<PropsFromRedux> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [login, setLogin] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [needOTP, setNeedOTP] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [isCapchaFilled, setIsCapchaFilled] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    props.logOut();
  }, []);

  useEffect(() => {
    setIsCapchaFilled(false);
    setupRecaptcha(() => {
      setMessage("");
      setIsCapchaFilled(true);
    });
  }, []);

  const loginSuccess = (user: FirebaseUser) => {
    if (user) {
      Sentry.setUser({ email: user?.user?.email });
      user.user.getIdToken().then((token) => {
        if (token) {
          props.clearErrorMessage();
          props.saveToken(token);
          props.logIn(user);
          setTimeout(() => {
            navigate(routes.V2_DASHBOARD_SCREEN);
            setLoading(false);
          }, 0);
        }
      });
    }
  };

  const signIn = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!isCapchaFilled) {
      setMessage(strings("login.captchaError"));
      return;
    }
    setLoading(true);
    setMessage("");

    if (!needOTP) {
      firebaseLogin(
        login,
        password,
        (user: FirebaseUser) => {
          loginSuccess(user);
        },
        () => {
          setNeedOTP(true);
          setLoading(false);
        },
        (err: Error) => {
          setMessage(err.message);
          setLoading(false);
        }
      );
    } else {
      verifyLoginOTP(otp)
        .then((user: FirebaseUser) => {
          loginSuccess(user);
        })
        .catch((err: Error) => {
          setMessage(err.message);
          setLoading(false);
        });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      signIn();
    }
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-flex-col lg:tw-flex-row">
      {/* Left Panel - Branding */}
      <div className="tw-hidden lg:tw-flex lg:tw-w-1/2 tw-bg-primary tw-flex-col tw-justify-between tw-p-12">
        <div>
          <img
            src={getWhiteLabelLogo()}
            alt="Logo"
            className="tw-h-10 tw-w-auto"
          />
        </div>
        <div className="tw-space-y-4">
          <h1 className="tw-text-4xl tw-font-bold tw-text-primary-foreground">
            {strings("login.brandingTitle")}
          </h1>
          <p className="tw-text-lg tw-text-primary-foreground/80">
            {strings("login.brandingSubtitle")}
          </p>
        </div>
        <p className="tw-text-sm tw-text-primary-foreground/60">
          © {new Date().getFullYear()} All rights reserved.
        </p>
      </div>

      {/* Right Panel - Login Form */}
      <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-background tw-p-6 lg:tw-p-12">
        <div className="tw-w-full tw-max-w-md tw-space-y-8">
          {/* Mobile Logo */}
          <div className="lg:tw-hidden tw-flex tw-justify-center">
            <div className="tw-bg-primary tw-p-4 tw-rounded-xl">
              <img
                src={getWhiteLabelLogo()}
                alt="Logo"
                className="tw-h-8 tw-w-auto"
              />
            </div>
          </div>

          <div className="tw-text-center lg:tw-text-left">
            <h2 className="tw-text-2xl tw-font-bold tw-tracking-tight">
              {needOTP ? strings("login.verifyTitle") : strings("login.title")}
            </h2>
            <p className="tw-text-muted-foreground tw-mt-2">
              {needOTP
                ? strings("login.verifySubtitle")
                : strings("login.subtitle")}
            </p>
          </div>

          <form onSubmit={signIn} className="tw-space-y-6">
            {message && (
              <Alert variant="destructive">
                <AlertCircle className="tw-h-4 tw-w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}

            {!needOTP ? (
              <div className="tw-space-y-4">
                <div className="tw-space-y-2">
                  <Label htmlFor="email">{strings("placeholders.email")}</Label>
                  <div className="tw-relative">
                    <Mail className="tw-absolute tw-left-3 tw-top-3 tw-h-4 tw-w-4 tw-text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={strings("placeholders.emailExample")}
                      value={login}
                      onChange={(e) => setLogin(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="tw-pl-10 tw-h-11"
                      autoComplete="email"
                      autoFocus={true}
                    />
                  </div>
                </div>

                <div className="tw-space-y-2">
                  <Label htmlFor="password">
                    {strings("placeholders.password")}
                  </Label>
                  <div className="tw-relative">
                    <Lock className="tw-absolute tw-left-3 tw-top-3 tw-h-4 tw-w-4 tw-text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={strings("login.passwordPlaceholder")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="tw-pl-10 tw-pr-10 tw-h-11"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="tw-absolute tw-right-3 tw-top-3 tw-text-muted-foreground hover:tw-text-foreground tw-transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="tw-h-4 tw-w-4" />
                      ) : (
                        <Eye className="tw-h-4 tw-w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="tw-space-y-2">
                <Label htmlFor="otp">{strings("login.otpLabel")}</Label>
                <div className="tw-relative">
                  <KeyRound className="tw-absolute tw-left-3 tw-top-3 tw-h-4 tw-w-4 tw-text-muted-foreground" />
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    placeholder={strings("login.otpPlaceholder")}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="tw-pl-10 tw-h-11 tw-text-center tw-text-lg tw-tracking-widest"
                    maxLength={6}
                    autoFocus={true}
                  />
                </div>
              </div>
            )}

            {/* Invisible reCAPTCHA container */}
            <div id="recaptcha-container"></div>

            <Button
              type="submit"
              className="tw-w-full tw-h-11"
              disabled={loading}
            >
              {loading && (
                <Loader2 className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" />
              )}
              {needOTP ? strings("login.verify") : strings("buttons.signIn")}
            </Button>

            <div className="tw-text-center">
              <Link
                to={routes.FORGOT_PASSWORD_SCREEN}
                className="tw-text-sm tw-text-primary hover:tw-underline"
              >
                {strings("login.forgotPassword")}
              </Link>
            </div>
          </form>

          <p className="tw-text-center tw-text-xs tw-text-muted-foreground lg:tw-hidden">
            {strings("login.terms")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default connector(LoginScreen);
